import React from 'react';
import clsx from 'clsx';
import classes from './OffersListingPage.module.scss';
import MerchantCard from 'components/molecules/MerchantCard/MerchantCard';
import { CategoryOption } from 'components/molecules/CategoryOptionsModal/CategoryOptionsModal';
import OtherOffers from 'components/molecules/NoOffers/OtherOffers';
import { EmptyStatus } from 'components/molecules/NoOffers';
import Notice from 'components/atoms/Notice/Notice';
import ErrorBoundary from 'components/molecules/ErrorBoundary/ErrorBoundary';
import { OfferItem } from './useOffers';
import { OfferType, getNoOffersMessage } from 'utils/offers';

interface Props {
  items: OfferItem[];
  promoItem?: OfferItem | null;
  categories: CategoryOption[];
  selectedCategory: string;
  searchQuery?: string;
  onCategorySelect: (tag: string) => void;
  className?: string;
  offerType: OfferType;
}

const OffersListingPage: React.FC<Props> = ({
  items,
  promoItem,
  categories,
  searchQuery = '',
  selectedCategory,
  onCategorySelect,
  className,
  offerType,
}) => {
  const errorNotice = (
    <Notice
      message={`We are having problems loading ${offerType} at the moment. Please try again later.`}
      status="warning"
    />
  );

  if (items.length === 0) {
    const selectedCategoryData = categories.find((cat) => cat.tag === selectedCategory);
    return (
      <div className={clsx(classes.root, className)}>
        <EmptyStatus
          {...getNoOffersMessage(
            offerType,
            selectedCategory === 'allCategories' ? undefined : selectedCategoryData?.name,
            searchQuery,
          )}
        />
        <OtherOffers
          categories={categories}
          onCategorySelect={onCategorySelect}
          currentCategory={selectedCategory}
          className={classes.otherOffers}
        />
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={errorNotice}>
      <div className={clsx(classes.root, className)}>
        <div className={classes.merchantCardsRow}>
          {!!promoItem && (
            <div key={promoItem.id} className={classes.highlightCard}>
              <MerchantCard
                merchantName={promoItem.name ?? ''}
                title={promoItem.title ?? ''}
                bannerImageSrc={promoItem.bannerImageSrc}
                offerUrl={promoItem.offerUrl}
                description={promoItem.description ?? ''}
                isHighlight={true}
                tags={promoItem.tags as string[]}
                offerType={offerType}
              />
            </div>
          )}
          {items.map((item) => (
            <div key={item.id} className={classes.normalCard}>
              <MerchantCard
                merchantName={item.name ?? ''}
                title={item.title ?? ''}
                bannerImageSrc={item.bannerImageSrc}
                offerUrl={item.offerUrl}
                description={item.description ?? ''}
                defaultImg="/coin.svg"
                tags={item.tags as string[]}
                offerType={offerType}
              />
            </div>
          ))}
        </div>
        <OtherOffers
          categories={categories}
          onCategorySelect={onCategorySelect}
          currentCategory={selectedCategory}
          className={classes.otherOffers}
        />
      </div>
    </ErrorBoundary>
  );
};

export default OffersListingPage;
